import { Ref, forwardRef, useMemo } from "react"
import { Form, Formik, FormikProps } from "formik"
import * as yup from "yup"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    MAX_DESCRIPTION_LENGTH,
    MAX_EMAIL_LENGTH,
    MAX_NAME_LENGTH,
} from "../../../../../../constants/inputValidationDefaults"

import { CreateUpdateWorkgroup, HistoryType, WorkgroupFormValues } from "../workgroupsTypes"
import { CreateUpdateWorkgroupFormContent } from "./CreateUpdateWorkgroupFormContent"

type CreateUpdateWorkgroupFormProps = Readonly<{
    initialValues?: WorkgroupFormValues | null
    history?: HistoryType
    onSubmit: (formData: CreateUpdateWorkgroup) => void
}>
type FormikRef = Ref<FormikProps<WorkgroupFormValues>>

function toCreateUpdateWorkgroup({ members, ...values }: WorkgroupFormValues): CreateUpdateWorkgroup {
    return {
        ...values,
        members: members.map((member) => ({ id: member.id })),
    }
}

export const CreateUpdateWorkgroupForm = forwardRef(
    ({ initialValues, history, onSubmit }: CreateUpdateWorkgroupFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const defaultInitialValues: WorkgroupFormValues = {
            name: "",
            description: "",
            calendarEmailAddress: "",
            isActive: true,
            members: [],
        }

        const validationSchema = useMemo(
            () =>
                yup.object({
                    name: yup
                        .string()
                        .trim()
                        .required(l10n.getString("create-or-edit-company-workgroup-field-is-required"))
                        .max(
                            MAX_NAME_LENGTH,
                            l10n.getString("create-or-edit-company-workgroup-field-name-max-length", {
                                maxLength: MAX_NAME_LENGTH,
                            }),
                        ),
                    calendarEmailAddress: yup
                        .string()
                        .trim()
                        .required(l10n.getString("create-or-edit-company-workgroup-field-is-required"))
                        .email(l10n.getString("create-or-edit-company-workgroup-field-invalid-email"))
                        .max(
                            MAX_EMAIL_LENGTH,
                            l10n.getString("create-or-edit-company-workgroup-field-calendar-email-max-length", {
                                maxLength: MAX_EMAIL_LENGTH,
                            }),
                        ),
                    description: yup.string().max(
                        MAX_DESCRIPTION_LENGTH,
                        l10n.getString("create-or-edit-company-workgroup-field-description-max-length", {
                            maxLength: MAX_DESCRIPTION_LENGTH,
                        }),
                    ),
                }),
            [l10n],
        )

        function handleSubmit(values: WorkgroupFormValues) {
            onSubmit(toCreateUpdateWorkgroup(values))
        }

        return (
            <Formik<WorkgroupFormValues>
                enableReinitialize
                initialValues={initialValues ? initialValues : defaultInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                <Form>
                    <CreateUpdateWorkgroupFormContent history={history} />
                </Form>
            </Formik>
        )
    },
)
